/**
 * ч.1110 (рішення власника 2026-10-03): підтвердження організації — це
 * ПІДПИСАННЯ ДОГОВОРУ з сервісом ключем КЕП організації (замість мовчазного
 * підтвердження при першій авторизації ключа). Попап: текст заяви про приєднання до оферти з
 * заповненими реквізитами підписання → галочка → ключ → підпис (CAdES у
 * браузері, ключ не покидає пристрій) → сервер звіряє, ЩО підписано і ЧИЇМ
 * ключем, зберігає заяву + підпис у Журналі ЕДО і ставить підтвердження.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, type Organization } from '@/api/catalogs';
import KeyAuthPanel from '@/shared/eusign/KeyAuthPanel';
import { MethodWidget, SignMethodTabs, type SignMethod } from '@/shared/eusign/SignMethodTabs';
import { bytesToBase64, loadEuSign, pickKeyForCode, signDataInternalCadesT } from '@/shared/eusign/eusign';
import { renderMarkdown } from '@/pages/help/markdown';
import { useToast } from '@/components/ToastProvider';
import { LegalDocLink } from '@/shared/components/LegalConsent';

interface Props {
  org: Pick<Organization, 'id' | 'name' | 'edrpou' | 'inn' | 'kep_confirmed_at'>;
  /** ч.1435: режим «ЕДО» — організацій там немає, є учасники ЕДО. */
  participant?: boolean;
  onClose: () => void;
}

const errText = (e: unknown): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : ((e as Error)?.message ?? String(e));
};

export default function ConfirmOrganizationDialog({ org, participant = false, onClose }: Props) {
  const of = participant ? 'учасника' : 'організації';
  const qc = useQueryClient();
  const toast = useToast();
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ч.1442: ключі сесії, хмарний ключ / токен або Дія.Підпис (у кого лише Дія — інакше не підтвердити).
  const [method, setMethod] = useState<SignMethod>('session');
  const code = org.edrpou || org.inn || '';

  // Сесія підписання живе 15 хв; текст приходить уже з реквізитами підписання.
  const signing = useQuery({
    queryKey: ['agreement-signing', org.id],
    queryFn: () => orgs.agreementSigning(org.id),
    staleTime: 0, gcTime: 0, retry: false,
  });
  const html = useMemo(
    () => (signing.data ? renderMarkdown(signing.data.document_text) : ''), [signing.data]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sign = useMutation({
    mutationFn: async () => {
      const s = signing.data!;
      await loadEuSign();
      await pickKeyForCode(code, org.name);
      const data = new TextEncoder().encode(s.document_text);
      const { signature, timestamped } = await signDataInternalCadesT(data);
      return orgs.confirm(org.id, {
        signing_id: s.signing_id, signature_b64: bytesToBase64(signature), timestamped,
      });
    },
    onSuccess: () => confirmed(),
    onError: (e) => setError(errText(e)),
  });
  function confirmed() {
    toast.show({ type: 'success', message: `${participant ? 'Учасника' : 'Організацію'} «${org.name}» підтверджено — заяву збережено у Журналі ЕДО` });
    void qc.invalidateQueries({ queryKey: ['orgs'] });
    void qc.invalidateQueries({ queryKey: ['orgs-for-modules'] });
    onClose();
  }

  const lock = !signing.data ? 'Текст заяви ще завантажується'
    : !agreed ? 'Поставте позначку, що ознайомились з офертою і заявою'
      : !code ? `У картці ${of} немає ЄДРПОУ/РНОКПП — заповніть і повторіть` : undefined;

  return (
    <div role="dialog" aria-modal="true" aria-label={`Підтвердження ${of} «${org.name}»`}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 50,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-panel)', color: 'var(--text)', borderRadius: 'var(--radius)',
        padding: '1.25rem 1.5rem', width: 820, maxWidth: '94vw', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0 }}>Підтвердження {of} «{org.name}»</h3>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
              Підтвердження — це приєднання до <LegalDocLink doc="terms">публічної оферти</LegalDocLink>: заяву підписують ключем КЕП {of}
              {code ? ` (код ${code})` : ''}. Підписана заява збережеться у Журналі ЕДО; після
              цього відкриються імпорт з 1С і відправка документів контрагентам.
              {org.kep_confirmed_at && ` ${participant ? 'Учасника' : 'Організацію'} вже підтверджено раніше — зараз ви лише підписуєте заяву.`}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрити">закрити</button>
        </div>

        <div style={{
          flex: '1 1 auto', minHeight: 160, overflow: 'auto', border: '1px solid var(--border)',
          borderRadius: 8, padding: '0.75rem 1rem', background: 'var(--bg-soft)', fontSize: '0.9rem',
        }}>
          {signing.isLoading && <div style={{ color: 'var(--muted)' }}>Завантаження тексту заяви…</div>}
          {signing.isError && (
            <div style={{ color: 'var(--badge-red-fg)' }} role="alert">{errText(signing.error)}</div>
          )}
          {signing.data && (
            // Текст заяви — наш (з сервера), екранується у renderMarkdown.
            <div data-testid="agreement-text" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>

        <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', cursor: 'pointer' }}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
            aria-label="Ознайомлений з офертою" />
          <span>
            Я ознайомився з офертою і <LegalDocLink doc="privacy">політикою конфіденційності</LegalDocLink> і підписую заяву від імені {of} «{org.name}» як
            уповноважена особа{signing.data ? ` (редакція ${signing.data.version})` : ''}.
          </span>
        </label>

        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Ключ КЕП {of}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Потрібен ключ, у сертифікаті якого ЄДРПОУ/РНОКПП збігається з кодом {of}.
            Ключ читається у браузері; пароль ключа на сервер не передається.
          </div>
          <SignMethodTabs value={method} onChange={setMethod} sessionLabel="ключ КЕП" />
          {method === 'session' ? <KeyAuthPanel /> : (
            <MethodWidget method={method}
              getData={async () => {
                if (lock) throw new Error(lock);
                setError(null);
                return new TextEncoder().encode(signing.data!.document_text);
              }}
              onSigned={async (sigB64) => {
                try {
                  await orgs.confirm(org.id, {
                    signing_id: signing.data!.signing_id, signature_b64: sigB64, timestamped: false,
                  });
                } catch (e) {
                  throw new Error(errText(e));
                }
                confirmed();
              }} />
          )}
        </div>

        {error && <div style={{ color: 'var(--badge-red-fg)' }} role="alert">{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={sign.isPending}>скасувати</button>
          {method === 'session' && (
            <button type="button" className="primary" disabled={!!lock || sign.isPending} title={lock}
              onClick={() => { setError(null); sign.mutate(); }}>
              {sign.isPending ? 'підписуємо…' : 'підписати заяву і підтвердити'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
